/**
 * The operator's own passkeys, for this domain.
 *
 * ## Why the CRM needs its own screen for this
 *
 * A passkey is bound to one registrable domain. The CRM is served from
 * `crm.webadmin.ee` and the account hub from `account.husly.app`, so a key
 * enrolled in the hub cannot sign anybody in here. Sending an operator to the
 * hub to "set up a passkey" would produce a key that does nothing on this
 * screen, which is worse than having no button at all - so the enrolment lives
 * where the login button is.
 *
 * The dialog says which host the key is being made for rather than leaving the
 * reader to infer it, because that one fact is the whole reason this list looks
 * empty right after somebody set a passkey up somewhere else.
 */
import { useCallback, useEffect, useState } from "react";
import { Fingerprint, Loader2, Trash2 } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { passkeyApi, isWebAuthnReady, isWebAuthnCancel, type Passkey } from "../../lib/passkey";
import { Btn, Modal, useToast } from "../ui";
import { fmtDate } from "../ui/format";

export function PasskeyDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const supported = isWebAuthnReady();
  const [rows, setRows] = useState<Passkey[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows((await passkeyApi.list()).passkeys);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function add() {
    setBusy(true);
    try {
      await passkeyApi.register();
      await load();
      toast(t("passkey.added"));
    } catch (err) {
      // A cancelled prompt is not a failure worth a toast.
      if (!isWebAuthnCancel(err)) toast(t("passkey.addFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    try {
      await passkeyApi.remove(id);
      await load();
      toast(t("passkey.removed"));
    } catch {
      toast(t("passkey.removeFailed"));
    } finally {
      setBusy(false);
    }
  }

  const host = typeof window === "undefined" ? "" : window.location.hostname;

  return (
    <Modal
      title={t("passkey.title")}
      onClose={onClose}
      footer={<Btn onClick={onClose}>{t("common.close")}</Btn>}
    >
      <div className="px-5 pb-2 pt-1">
        <h3 className="text-[13.25px] font-[650]">{t("passkey.heading")}</h3>
        <p className="crm-mut mt-1 text-[13px]">{t("passkey.intro", { host })}</p>

        {rows === null ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="crm-mut py-6 text-center text-[13px]">{t("passkey.empty")}</p>
        ) : (
          <ul className="my-3 flex flex-col gap-2">
            {rows.map((k) => (
              <li
                key={k.id}
                className="flex items-center gap-3 rounded-2xl border border-border px-3.5 py-2.5"
              >
                <Fingerprint className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.25px] font-[650]">
                    {k.deviceName || t("passkey.unnamed")}
                  </span>
                  <span className="crm-mut block text-[11.5px]">
                    {k.lastUsedAt
                      ? t("passkey.lastUsed", { date: fmtDate(k.lastUsedAt) })
                      : t("passkey.addedAt", { date: fmtDate(k.createdAt) })}
                  </span>
                </span>
                <button
                  type="button"
                  className="crm-mi-danger shrink-0 rounded-full p-2 transition-colors hover:bg-destructive/10"
                  aria-label={t("passkey.remove")}
                  disabled={busy}
                  onClick={() => void remove(k.id)}
                >
                  <Trash2 className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {supported ? (
          <Btn tone="brand" className="w-full" disabled={busy} onClick={() => void add()}>
            <Fingerprint className="size-4" />
            {t("passkey.add")}
          </Btn>
        ) : (
          <p className="crm-mut text-[13px]">{t("passkey.unsupported")}</p>
        )}
      </div>
    </Modal>
  );
}
